import { useUiStore } from '@/features/settings/ui-store.js';
import {
  readTeamsSubPageId,
  teamsDeepLinkTarget,
} from '@/integration/runtime/teams-deep-link.js';
import { fetchPreferences } from '@/lib/api/preferences.js';
import { withTimeout } from '@/lib/with-timeout.js';
import { logger } from '@/logger.js';

import { parseLastLocation } from './restorable-location.js';

/** The app shows nothing while it decides where to open, so the decision gets a short budget. */
export const STARTUP_RESTORE_BUDGET_MS = 1_500;

const HOME_HREF = '/home';

let entryPathname =
  typeof window === 'undefined' ? '/' : window.location.pathname;
let startupConsumed = false;
let fellBack = false;

/**
 * Where the app opens when it is loaded at its root, which is how Teams opens the tab and how a bare
 * address lands. The first time in a page load it reopens the page the user had open last, on any
 * device, together with that page's workspace; a Teams notification link wins over it, and every
 * later visit to the root (a deleted chat, a logo link) is plain Home. Anything slow or broken also
 * ends at Home, so the app never waits long for this.
 */
export async function resolveStartupHref(): Promise<string> {
  const restoreAllowed = !startupConsumed && entryPathname === '/';
  startupConsumed = true;
  if (!restoreAllowed) return HOME_HREF;
  try {
    const [subPageId, preferences] = await withTimeout(
      Promise.all([readTeamsSubPageId(), fetchPreferences()]),
      STARTUP_RESTORE_BUDGET_MS,
      'Restoring the last page',
    );
    if (teamsDeepLinkTarget(subPageId) !== null) return HOME_HREF;
    return storedHref(preferences.last_location);
  } catch (error: unknown) {
    fellBack = true;
    logger.debug('startup-location: opening Home instead', error);
    return HOME_HREF;
  }
}

/** The stored page to open, after selecting its workspace; Home when there is nothing to reopen. */
function storedHref(raw: unknown): string {
  const location = parseLastLocation(raw);
  if (location === null) return HOME_HREF;
  if (location.workspaceId !== null) {
    useUiStore
      .getState()
      .setSelectedWorkspaceId(location.workspaceId, 'system');
  }
  return location.href;
}

/**
 * True when this page load opened Home only because the stored page could not be read in time. Home
 * is then not a choice the user made, and must not be recorded over the page they really left.
 */
export function wasStartupFallback(): boolean {
  return fellBack;
}

/** Test-only: a fresh page load that entered at `entry`. */
export function _resetStartupLocationForTests(entry = '/'): void {
  entryPathname = entry;
  startupConsumed = false;
  fellBack = false;
}
